/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Program } from '@/api/models';
import { orderDataSets } from './buildRelease';
import { shortDbDate } from './dbDate';
import { useIsInternalUser } from './userRole';

/** A dropdown choice whose label carries more context than the value it stores. */
export interface ReleaseOption {
  value: string;
  label: string;
}

/** The year/make/model whose data sets the field offers. */
export interface VehicleSelection {
  modelYear: string;
  make: string;
  model: string;
}

/** What the data-set field should render, and which data set the search has settled on. */
export interface DataSetField {
  /** The choices on offer, newest build release first. */
  options: ReleaseOption[];
  /**
   * The release the search should open: the one picked, when the catalog still offers it, or the
   * only one there is.
   */
  value: string;
  /**
   * Whether the field is worth rendering.
   *
   * A dealer only gets it where there is a decision to make. With one data set there is nothing to
   * choose and "Data set 1" explains nothing, so the field goes rather than standing there filled
   * in. Internal users keep it, because which release is open is worth reading in itself.
   */
  visible: boolean;
}

/**
 * The distinct data sets serving a vehicle, newest build release first.
 *
 * Ordered by {@link orderDataSets} rather than by date alone so this agrees with the data-set
 * dialog. Two releases of one vehicle are routinely published on the same dealer schedule date,
 * and ordering those on the date leaves the winner to whichever row the catalog returned first --
 * which is how the same pair came to be numbered one way here and the other way in the dialog.
 *
 * Collapsed to one entry per release code, because the code is what the field stores and what the
 * program lookup and the URL contract match on. A repeated code keeps its newest program.
 */
export function orderedReleases(programs: Program[]): Program[] {
  const seen = new Set<string>();
  return orderDataSets(programs).filter((program) => {
    const code = program.engineeringRelease;
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

/**
 * The data-set field for one year/make/model: what to offer, what is chosen, whether to show it.
 *
 * Shared by the home search card and the top-bar picker so the two cannot drift: both offer the
 * same data sets in the same order under the same names, and both settle on a lone data set
 * without making the technician confirm a dropdown that has one legal answer.
 *
 * Labels follow the data-set dialog. Internal users read the release code; dealers read the
 * ordinal, because program code and engineering release are internal metadata. Both carry the date
 * the wiring data took effect, which is what tells two builds of one vehicle apart.
 */
export function useDataSetField(
  programs: Program[],
  vehicle: VehicleSelection,
  picked: string,
): DataSetField {
  const { t, i18n } = useTranslation();
  const internal = useIsInternalUser();
  const locale = i18n.language;
  const { modelYear, make, model } = vehicle;

  const options = useMemo<ReleaseOption[]>(() => {
    const matches = orderedReleases(
      programs.filter((p) => p.modelYear === modelYear && p.make === make && p.model === model),
    );
    return matches.map((program, index) => {
      const name = internal
        ? program.engineeringRelease
        : t('explorer.dataSet.option', { number: index + 1 });
      const effective = shortDbDate(program.dataEffectiveDate, locale);
      return {
        value: program.engineeringRelease,
        label: effective ? `${name} - ${effective}` : name,
      };
    });
  }, [programs, modelYear, make, model, internal, locale, t]);

  // A restored draft can name a release this vehicle no longer offers. Keeping it would leave a
  // dealer -- whose field is hidden when there is only one data set -- holding a value they cannot
  // see and cannot change, and the search would never resolve. An unknown pick is dropped; a lone
  // data set still settles on its own.
  const known = options.some((option) => option.value === picked);
  return {
    options,
    value: known ? picked : options.length === 1 ? options[0].value : '',
    visible: internal || options.length > 1,
  };
}
